import React, { useState, useEffect, useMemo, useRef } from 'react'
import { concatStyles, getLabel } from 'src/utils'
import { LabelOption } from 'src/@types'
import { useStyles } from './styles'
import { Checkbox } from '@material-ui/core'

interface MultiSelectInputProps {
  label: string
  value: Array<string | number>
  options: LabelOption[]
  selectBy: string
  showBy: string[] | any
  placeholder?: string
  onChange: (selected: Array<string | number>) => void
  error?: string
  className?: string
  disabled?: boolean
  id?: string
  onLoad?: (selected: LabelOption[]) => void
}

export const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  options,
  value,
  label,
  selectBy,
  showBy,
  placeholder,
  onChange,
  error,
  className,
  onLoad,
  ...rest
}) => {
  const classes = useStyles()

  const [filtered, setFiltered] = useState<LabelOption[]>([])
  const [inputValue, setInputValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const [selectedValues, setSelectedValues] = useState<LabelOption[]>([])

  const listRef = useRef<HTMLUListElement>(null)

  const list = useMemo(() => options, [options])
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (value?.length) {
      const selected = list.filter((item) => value.includes(item[selectBy]))
      setSelectedValues(selected)
      onLoad?.(selected)
    } else {
      setSelectedValues([])
    }

    setFiltered(list)
  }, [value, list, selectBy, onLoad])

  useEffect(() => {
    setInputValue(selectedValues.map((item) => getLabel(item, showBy)).join(', '))
  }, [selectedValues, showBy])

  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return

      if (!containerRef.current.contains(event.target as Node)) {
        setFocused(false)
        setHighlightedIndex(-1)
      }
    }

    const handleFocusChange = (event: FocusEvent) => {
      if (!containerRef.current) return

      if (!containerRef.current.contains(event.target as Node)) {
        setFocused(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('pointerdown', handleClickOutside)
    document.addEventListener('focusin', handleFocusChange)

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside)
      document.removeEventListener('focusin', handleFocusChange)
    }
  }, [])

  const handleChangeInput = (value: string) => {
    setInputValue(value)

    const newList = list.filter((item) => getLabel(item, showBy).toUpperCase().includes(value.toUpperCase()))

    setFiltered(newList)
    setHighlightedIndex(-1)
  }

  const handleCheckboxChange = (option: LabelOption) => {
    const isSelected = selectedValues.some((item) => item[selectBy] === option[selectBy])

    let newSelected: LabelOption[]

    if (isSelected) {
      newSelected = selectedValues.filter((item) => item[selectBy] !== option[selectBy])
    } else {
      newSelected = [...selectedValues, option]
    }

    setSelectedValues(newSelected)

    const result = newSelected.map((item) => item[selectBy])
    onChange(result)
  }

  const handleSelectAll = () => {
    if (selectedValues.length === list.length) {
      setSelectedValues([])
      onChange([])
    } else {
      setSelectedValues(list)
      onChange(list.map((item) => item[selectBy]))
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1))
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
    }

    if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault()
      handleCheckboxChange(filtered[highlightedIndex])
    }
  }

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget as HTMLElement | null

    if (next?.closest('.multi_select-input__dropper')) {
      return
    }

    setFocused(false)
    setHighlightedIndex(-1)

    setInputValue(selectedValues.map((item) => getLabel(item, showBy)).join(', '))
  }

  return (
    <div className={concatStyles(classes.filterButtonWrapper, className)} ref={containerRef}>
      <label className='multi_select-input__label'>{label}</label>

      <input
        autoComplete='off'
        value={inputValue}
        placeholder={placeholder}
        className='multi_select-input__input'
        onFocus={() => setFocused(true)}
        onBlur={handleOnBlur}
        onChange={(e) => handleChangeInput(e.target.value)}
        onKeyDown={handleKeyDown}
        {...rest}
      />

      {error && <span className='multi_select-input__error'>{error}</span>}

      {focused && (
        <ul className='multi_select-input__dropper' ref={listRef} tabIndex={-1}>
          {filtered.length > 0 && (
            <li className='multi_select-input__option' onClick={handleSelectAll}>
              <Checkbox className={classes.checkbox} checked={selectedValues.length === list.length} />
              Selecionar Todos
            </li>
          )}

          {filtered.map((option, index) => (
            <li
              key={option[selectBy]}
              className={`multi_select-input__option ${highlightedIndex === index ? 'multi_highlighted' : ''}`}
              onClick={() => handleCheckboxChange(option)}
            >
              <Checkbox
                className={classes.checkbox}
                checked={selectedValues.some((item) => item[selectBy] === option[selectBy])}
              />
              {getLabel(option, showBy)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
